// Generated by scripts/generate-seo-content.mts. Do not edit.

import type { SeoPageMetadata } from "@/lib/seo-content-types";

type FooterSeoPage = { label: string; order: number; slug: string };

export const seoPages: readonly SeoPageMetadata[] = [
  {
    "slug": "ai-assisted-decision-making",
    "type": "guide",
    "status": "published",
    "title": "AI-Assisted Decision Making: How Teams Make Better Decisions",
    "seoTitle": "AI-Assisted Decision Making for Teams: A Framework",
    "description": "Use AI to compare options and challenge assumptions while people own the decision. Includes a copyable decision template and a worked build-versus-buy example.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-09-07",
    "canonical": "https://boardblend.com/resources/ai-assisted-decision-making",
    "author": "Boardblend",
    "hero": {
      "src": "/seo/ai-assisted-decision-making/header.webp",
      "alt": "Tactile evidence streams passing through a compass toward a grounded shared decision"
    },
    "footer": {
      "visible": false
    },
    "faqs": [
      {
        "question": "Can AI make better decisions than a team?",
        "answer": "AI can improve parts of a process by organizing evidence, generating alternatives, and challenging assumptions. Decision quality still depends on valid context, accountable people, relevant expertise, and verification."
      },
      {
        "question": "What decisions should never be delegated entirely to AI?",
        "answer": "Do not delegate accountability for legal, medical, employment, safety, security, financial, or other high-impact decisions to a general AI system. Use qualified review, approved systems, and applicable governance."
      },
      {
        "question": "How can teams prevent automation bias?",
        "answer": "Capture independent judgments before revealing AI advice, show uncertainty and sources, require counterarguments, make override responsibility explicit, and track when people accept or reject model suggestions."
      },
      {
        "question": "Can multiple AI agents improve a team decision?",
        "answer": "Multiple agents can widen perspectives when they use relevant context and distinct roles. They can also repeat the same error, so compare evidence and assumptions rather than treating agreement between models as proof."
      },
      {
        "question": "How should a team measure decision quality?",
        "answer": "Track process quality, outcome quality, and learning speed. Review whether relevant perspectives entered, assumptions were tested, the expected outcome occurred, and the team updated its model when new evidence arrived."
      },
      {
        "question": "How does AI help in decision making?",
        "answer": "AI can organize evidence, generate alternatives, expose assumptions, compare scenarios, challenge a preferred option, and draft a decision record. People must still verify claims, weigh consequences, and own the decision."
      },
      {
        "question": "What is an example of AI-assisted decision making?",
        "answer": "A product team can give AI customer evidence, engineering constraints, and decision criteria, then use it to compare build, buy, and test options. A named human owner verifies the analysis and records the final choice."
      }
    ]
  },
  {
    "slug": "ai-brainstorming-tools",
    "type": "roundup",
    "status": "published",
    "title": "Best AI Brainstorming Tools for Teams in 2026",
    "seoTitle": "10 Best AI Brainstorming Tools for Teams (2026)",
    "description": "Compare the best free and paid AI brainstorming tools for team workshops, parallel ideation, shared context, visual collaboration, and clearer decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-09-07",
    "canonical": "https://boardblend.com/resources/ai-brainstorming-tools",
    "author": "Boardblend",
    "reviewedAt": "2026-08-01",
    "hero": {
      "src": "/seo/ai-brainstorming-tools/header.webp",
      "alt": "A tactile creative toolkit with idea cards, a lens, clustering rings, and a shared canvas"
    },
    "footer": {
      "visible": true,
      "label": "AI Brainstorming Tools",
      "order": 1
    },
    "faqs": [
      {
        "question": "What is the best AI brainstorming tool for teams?",
        "answer": "Boardblend is the focused option for parallel teammate and agent contributions that converge on one decision. Miro is stronger for broad whiteboarding, FigJam for design teams, and ChatGPT or Claude for individual exploration."
      },
      {
        "question": "What are the best free brainstorming tools for teams?",
        "answer": "Boardblend is a focused free option for one active team brainstorm, while Miro and FigJam provide broader free visual workspaces with plan limits. Compare collaboration, AI allowances, privacy, exports, and whether the workflow records a decision before choosing."
      },
      {
        "question": "Can teammates use different AI models in one brainstorm?",
        "answer": "Yes, if the workflow supports separate connections and approved data handling. Boardblend is designed around bring-your-own agents, while other tools may provide built-in AI, shared workspaces, or external connectivity."
      },
      {
        "question": "What features matter most for remote team brainstorming?",
        "answer": "Prioritize independent contribution, shared context, clear provenance, asynchronous access, facilitation, evidence links, structured evaluation, decision capture, permissions, accessibility, and reliable exports."
      },
      {
        "question": "How is an AI canvas different from an AI chat?",
        "answer": "A visual canvas keeps options, evidence, relationships, and disagreements visible at once. Chat is excellent for sequential exploration, but important context and parallel viewpoints can become buried in a transcript."
      },
      {
        "question": "How were these AI brainstorming tools evaluated?",
        "answer": "We applied one product-roadmap workshop to each category and compared participation, context, divergence, synthesis, visual structure, decision capture, facilitation, integrations, governance, setup, and value as of August 1, 2026."
      }
    ]
  },
  {
    "slug": "boardblend-vs-chatgpt",
    "type": "comparison",
    "status": "published",
    "title": "Boardblend vs ChatGPT for Team Brainstorming",
    "seoTitle": "Boardblend vs ChatGPT for Team Brainstorming",
    "description": "Compare Boardblend and ChatGPT for brainstorming, shared context, parallel team input, visual organization, multiple agents, and clearer team decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/boardblend-vs-chatgpt",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "competitor": "ChatGPT",
    "hero": {
      "src": "/seo/boardblend-vs-chatgpt/header.webp",
      "alt": "A deep sequential paper tunnel contrasted with parallel streams weaving into a shared workspace"
    },
    "footer": {
      "visible": true,
      "label": "Boardblend vs ChatGPT",
      "order": 5
    },
    "faqs": [
      {
        "question": "Is Boardblend an AI model?",
        "answer": "No. Boardblend is a collaborative visual workspace that coordinates people and the AI agents they choose. The quality and capabilities of an agent still depend on its underlying model and tools."
      },
      {
        "question": "Does Boardblend replace ChatGPT?",
        "answer": "Usually not. ChatGPT is a broad AI assistant for research, analysis, writing, coding, and individual exploration. Boardblend adds a team layer for parallel contribution, shared visual context, and decision convergence."
      },
      {
        "question": "Can teammates bring ChatGPT into Boardblend?",
        "answer": "Boardblend is designed for bring-your-own-agent connections, so ChatGPT can be part of a participant's workflow where a supported, approved connection is available. Verify the current integration and workspace policies."
      },
      {
        "question": "How is a visual canvas different from a shared ChatGPT project?",
        "answer": "A shared project centralizes files, instructions, and chats. A visual canvas keeps several participants' options, evidence, relationships, disagreements, and final rationale visible together rather than across chat branches."
      },
      {
        "question": "Which is better for individual brainstorming or team decisions?",
        "answer": "ChatGPT is generally better for flexible individual exploration. Boardblend is better when several people and agents need to contribute in parallel and preserve how the team reached a decision."
      }
    ]
  },
  {
    "slug": "boardblend-vs-figjam",
    "type": "comparison",
    "status": "published",
    "title": "Boardblend vs FigJam for Team Brainstorming",
    "seoTitle": "Boardblend vs FigJam for AI Team Brainstorming",
    "description": "Compare Boardblend and FigJam for AI brainstorming, visual collaboration, independent input, design workflows, cross-functional evidence, and decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/boardblend-vs-figjam",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "competitor": "FigJam",
    "hero": {
      "src": "/seo/boardblend-vs-figjam/header.webp",
      "alt": "Playful design pieces contrasted with cross-functional tokens forming a stable decision sculpture"
    },
    "footer": {
      "visible": false
    },
    "faqs": [
      {
        "question": "Is Boardblend a FigJam alternative?",
        "answer": "Boardblend is a focused FigJam alternative for AI-assisted cross-functional decisions, but not a complete replacement for design workshops, freeform drawing, Figma adjacency, or FigJam's multiplayer facilitation features."
      },
      {
        "question": "Which product is better for designers?",
        "answer": "FigJam is usually better for designers because it sits beside Figma and supports familiar visual ideation, critique, diagrams, reactions, voting, and direct movement toward design work."
      },
      {
        "question": "Does FigJam support AI brainstorming?",
        "answer": "Yes. FigJam AI can generate workshop materials, diagrams, timelines, and plans, then sort and summarize sticky notes. Availability and AI credits depend on current plans and seats."
      },
      {
        "question": "Which product handles cross-functional decisions better?",
        "answer": "Boardblend is more focused on separate product, design, engineering, and agent perspectives, visible disagreement, shared evidence, and a final rationale. FigJam can support this when a facilitator builds the process."
      },
      {
        "question": "Can Boardblend output move into Figma?",
        "answer": "Teams can use the Boardblend decision and rationale to guide Figma work, but verify current export or integration support for the exact handoff. FigJam remains the more native option inside the Figma ecosystem."
      }
    ]
  },
  {
    "slug": "boardblend-vs-lucidspark",
    "type": "comparison",
    "status": "published",
    "title": "Boardblend vs Lucidspark for Team Brainstorming",
    "seoTitle": "Boardblend vs Lucidspark: Team Workflow Compared",
    "description": "Compare Boardblend and Lucidspark for AI brainstorming, visual collaboration, facilitation, diagramming, shared evidence, and documented team decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/boardblend-vs-lucidspark",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "competitor": "Lucidspark",
    "hero": {
      "src": "/seo/boardblend-vs-lucidspark/header.webp",
      "alt": "Structured process blocks contrasted with evidence paths joining a shared decision hub"
    },
    "footer": {
      "visible": false
    },
    "faqs": [
      {
        "question": "Is Boardblend a Lucidspark alternative?",
        "answer": "Boardblend is a focused Lucidspark alternative for human-and-agent team decisions, but it does not replace Lucidspark's broader whiteboarding, facilitation, diagramming connections, and Lucid ecosystem."
      },
      {
        "question": "Which product is better for diagramming?",
        "answer": "Lucidspark is better for diagramming-oriented workflows, especially when ideas should move into Lucidchart, formal process maps, system diagrams, or structured visual documentation."
      },
      {
        "question": "Does Lucidspark have AI brainstorming features?",
        "answer": "Yes. Lucid AI can generate and expand ideas, sort content into themes, summarize sessions, build mind maps, and work with canvas context. Availability and future pricing may change."
      },
      {
        "question": "Which product is better for cross-functional decisions?",
        "answer": "Boardblend is more focused on separate functional and agent perspectives, shared evidence, visible disagreement, and a final rationale. Lucidspark can support decisions through facilitation, voting, tables, and task handoff."
      },
      {
        "question": "Can teams use Boardblend and Lucidspark together?",
        "answer": "Yes. Boardblend can hold the decision rationale while Lucidspark and Lucidchart document the resulting process or system. Name one authoritative record and link the artifacts."
      }
    ]
  },
  {
    "slug": "boardblend-vs-miro",
    "type": "comparison",
    "status": "published",
    "title": "Boardblend vs Miro for AI Team Brainstorming",
    "seoTitle": "Boardblend vs Miro: AI Team Brainstorming Compared",
    "description": "Compare Boardblend and Miro for AI brainstorming, shared context, visual collaboration, personal agent workflows, facilitation, and team decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/boardblend-vs-miro",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "competitor": "Miro",
    "hero": {
      "src": "/seo/boardblend-vs-miro/header.webp",
      "alt": "A sprawling visual workspace contrasted with a focused bridge carrying people and AI toward one outcome"
    },
    "footer": {
      "visible": true,
      "label": "Boardblend vs Miro",
      "order": 4
    },
    "faqs": [
      {
        "question": "Is Boardblend a complete replacement for Miro?",
        "answer": "No. Miro covers broader whiteboarding, diagramming, planning, prototypes, integrations, and enterprise administration. Boardblend is a focused alternative for parallel human-and-agent brainstorming that ends in a clear decision."
      },
      {
        "question": "Does Miro support external AI agents?",
        "answer": "Yes. Miro offers an MCP server on every plan with plan-specific call limits and supports AI tools such as Claude, Cursor, GitHub Copilot, and Gemini CLI."
      },
      {
        "question": "Which product is better for facilitated workshops?",
        "answer": "Miro is stronger for general facilitation because it offers mature timers, voting, private mode, visitor editing, templates, and activities. Boardblend is stronger when the workshop specifically centers on participant-owned agents and decision convergence."
      },
      {
        "question": "Which product is simpler for AI-assisted team decisions?",
        "answer": "Boardblend is more focused and requires less process design for its core use case. Miro can run a strong decision workshop, but the facilitator chooses and configures the board, modes, AI actions, and decision template."
      },
      {
        "question": "Can a team use Boardblend and Miro together?",
        "answer": "Yes. A team can retain Miro for broad visual planning and use Boardblend for consequential AI-assisted decisions. Avoid duplicating the authoritative decision record across both products."
      }
    ]
  },
  {
    "slug": "boardblend-vs-mural",
    "type": "comparison",
    "status": "published",
    "title": "Boardblend vs Mural for AI-Assisted Teamwork",
    "seoTitle": "Boardblend vs Mural: AI Decisions or Workshops?",
    "description": "Compare Boardblend and Mural for AI-assisted brainstorming, workshop facilitation, shared context, parallel participation, and documented team decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/boardblend-vs-mural",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "competitor": "Mural",
    "hero": {
      "src": "/seo/boardblend-vs-mural/header.webp",
      "alt": "A facilitated workshop table contrasted with parallel contributors gathering around a shared outcome"
    },
    "footer": {
      "visible": false
    },
    "faqs": [
      {
        "question": "Is Boardblend a complete Mural replacement?",
        "answer": "No. Mural offers broader facilitation controls, templates, visual methods, enterprise administration, and Microsoft ecosystem connections. Boardblend is a focused alternative for participant-owned agents and traceable team decisions."
      },
      {
        "question": "Which product is better for professional facilitators?",
        "answer": "Mural is generally stronger for professional facilitators because of private mode, anonymous voting, timer, summon and control features, hide and reveal, templates, and established workshop methods."
      },
      {
        "question": "Does Mural have AI brainstorming features?",
        "answer": "Yes. As reviewed July 26, 2026, Mural AI can generate and expand ideas, create mind maps, cluster content, summarize, classify sentiment, and support guided AI workflows on paid plans."
      },
      {
        "question": "Which product supports teammate-owned AI agents?",
        "answer": "Boardblend is designed so each participant can connect a preferred AI agent to the shared brainstorm. Mural documents one integrated Mural AI experience and Microsoft 365 Copilot connections rather than participant-specific parallel agents."
      },
      {
        "question": "Which product is better for documenting decisions?",
        "answer": "Boardblend is more opinionated about preserving source perspectives, trade-offs, rationale, dissent, owner, and next action. Mural can document decisions well when the facilitator builds that structure into the workshop."
      }
    ]
  },
  {
    "slug": "feature-prioritization",
    "type": "guide",
    "status": "published",
    "title": "How to Prioritize Features With Product, Design and Engineering",
    "seoTitle": "Feature Prioritization With Product, Design & Engineering",
    "description": "Prioritize features using developer feedback, customer evidence, and clear low, medium, and high scores. Includes a copyable worksheet and worked example.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-09-07",
    "canonical": "https://boardblend.com/resources/feature-prioritization",
    "author": "Boardblend",
    "hero": {
      "src": "/seo/feature-prioritization/header.webp",
      "alt": "Four hands contributing product, design, and engineering tokens to a balanced feature priority"
    },
    "footer": {
      "visible": true,
      "label": "Feature Prioritization",
      "order": 6
    },
    "faqs": [
      {
        "question": "What is the best feature prioritization framework?",
        "answer": "Use the lightest framework that makes the real trade-off visible. Value versus effort works for a small uncertain set, RICE helps compare reach and impact, and weighted scoring helps when several explicit constraints matter."
      },
      {
        "question": "What role should engineering play in feature prioritization?",
        "answer": "Engineering should contribute delivery ranges, dependencies, operational risks, reversibility, platform leverage, and simpler alternatives. It should not be asked only to estimate a solution after priorities are already decided."
      },
      {
        "question": "How should teams resolve conflicting feature scores?",
        "answer": "Discuss the evidence, definition, confidence, and assumptions behind the scores before averaging them. A large difference often reveals useful domain knowledge or a differently understood problem."
      },
      {
        "question": "When should a product team use RICE prioritization?",
        "answer": "RICE is useful when initiatives have reasonably comparable estimates for reach, impact, confidence, and effort. Avoid it when inputs are mostly guesses or when strategic, regulatory, or dependency constraints dominate."
      },
      {
        "question": "Can AI prioritize a product backlog?",
        "answer": "AI can organize evidence, challenge assumptions, and calculate an agreed model. People must still validate the inputs, choose the criteria, own the trade-offs, and record the decision."
      }
    ]
  },
  {
    "slug": "groupthink-examples",
    "type": "guide",
    "status": "published",
    "title": "Groupthink Examples—and How to Avoid It During Team Brainstorming",
    "seoTitle": "Groupthink Examples—and How to Avoid It at Work",
    "description": "See practical groupthink examples from everyday team decisions, learn the warning signs, and use a repeatable process that protects independent thinking.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/groupthink-examples",
    "author": "Boardblend",
    "hero": {
      "src": "/seo/groupthink-examples/header.webp",
      "alt": "Identical paper planes following one route contrasted with distinct paths reaching a shared destination"
    },
    "footer": {
      "visible": false
    },
    "faqs": [
      {
        "question": "What is a simple example of groupthink at work?",
        "answer": "A product team agrees with the first feature proposed by its leader, even though several members privately see customer, design, or technical risks. The apparent consensus reflects social pressure and anchoring rather than independent agreement."
      },
      {
        "question": "What are the main warning signs of groupthink?",
        "answer": "Common signs include unusually fast agreement, few alternatives, self-censorship, pressure on dissenters, confidence without evidence, and a decision record that contains no risks or minority views."
      },
      {
        "question": "Does groupthink mean a team should avoid consensus?",
        "answer": "No. Consensus can be a useful outcome. The risk is premature consensus reached before people have contributed independent information, challenged assumptions, and compared alternatives against explicit criteria."
      },
      {
        "question": "How can a facilitator reduce groupthink during brainstorming?",
        "answer": "Ask people to think independently before discussion, collect contributions in parallel, delay the leader’s opinion, assign a challenger, evaluate ideas against shared criteria, and record unresolved concerns with the final decision."
      },
      {
        "question": "Can AI make groupthink worse?",
        "answer": "Yes. If everyone sees the same AI answer first, it can become a powerful anchor. AI is safer when teammates use it to develop or challenge independent perspectives before the group combines and evaluates the results."
      }
    ]
  },
  {
    "slug": "how-to-brainstorm-with-ai",
    "type": "guide",
    "status": "published",
    "title": "How to Brainstorm With AI as a Team",
    "seoTitle": "How to Brainstorm With AI as a Team: 7 Steps",
    "description": "Use a practical template and prompts to brainstorm with AI as a team, preserve independent perspectives, and finish with a clear decision.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-09-07",
    "canonical": "https://boardblend.com/resources/how-to-brainstorm-with-ai",
    "author": "Boardblend",
    "hero": {
      "src": "/seo/how-to-brainstorm-with-ai/header.webp",
      "alt": "Team hands and small luminous AI companions arranging distinct ideas on one shared workspace"
    },
    "footer": {
      "visible": true,
      "label": "Brainstorm With AI",
      "order": 2
    },
    "faqs": [
      {
        "question": "Should people brainstorm independently before using AI?",
        "answer": "Yes. A short independent pass protects each person’s knowledge and judgment from being anchored by the first visible AI answer. AI can then expand, challenge, or test those original views."
      },
      {
        "question": "Can teammates use different AI agents in one brainstorm?",
        "answer": "Yes. Different agents can contribute useful perspectives when they receive the same decision context and their outputs remain attributable. The team should compare evidence and assumptions rather than vote for a favourite model."
      },
      {
        "question": "What context should an AI brainstorming session include?",
        "answer": "Give every participant the decision, desired outcome, audience, evidence, constraints, known risks, and definition of success. Exclude confidential data that the selected AI service is not approved to process."
      },
      {
        "question": "How long should an AI team brainstorm take?",
        "answer": "A focused decision can use the 30-minute agenda in this guide. Complex or high-risk decisions need more time for evidence gathering, fact-checking, stakeholder review, and a separate approval step."
      },
      {
        "question": "How do teams prevent AI-driven convergence?",
        "answer": "Capture independent human views first, let agents challenge rather than replace them, delay synthesis until all contributions are visible, and preserve disagreements alongside the final decision."
      },
      {
        "question": "Can I use AI to brainstorm ideas without letting it take over?",
        "answer": "Yes. Write your initial view first, then ask AI for different mechanisms, missing stakeholders, counterarguments, and evidence gaps. Keep the original human contribution visible and verify factual claims before using them."
      }
    ]
  },
  {
    "slug": "miro-alternatives",
    "type": "roundup",
    "status": "published",
    "title": "The Best Miro Alternatives for AI Brainstorming",
    "seoTitle": "7 Best Miro Alternatives for AI Brainstorming (2026)",
    "description": "Compare Miro alternatives for AI team brainstorming, visual collaboration, facilitation, shared context, agent workflows, pricing, and clearer decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/miro-alternatives",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "hero": {
      "src": "/seo/miro-alternatives/header.webp",
      "alt": "Many wandering creative paths resolving into one focused green route toward a clear outcome"
    },
    "footer": {
      "visible": true,
      "label": "Miro Alternatives",
      "order": 3
    },
    "faqs": [
      {
        "question": "What is the best Miro alternative for AI brainstorming?",
        "answer": "Boardblend is the focused choice when teammates and their preferred AI agents need to contribute in parallel and converge on a shared decision. Mural, FigJam, and Lucidspark are stronger fits for different workshop, design, or diagramming needs."
      },
      {
        "question": "Does Miro support AI and MCP?",
        "answer": "Yes. As of July 26, 2026, Miro offers AI generation, clustering, summaries, workflows, agents, and an MCP server on every plan with plan-specific credit and call limits."
      },
      {
        "question": "Which Miro alternatives support free visual collaboration?",
        "answer": "Miro, FigJam, Lucidspark, Whimsical, Excalidraw, ClickUp, and Boardblend all provide some form of free access or trial, but limits and guest rules vary. Verify current pricing before choosing."
      },
      {
        "question": "Can a team use personal AI agents instead of built-in whiteboard AI?",
        "answer": "Miro supports external AI tools through MCP. Boardblend focuses on participant-specific bring-your-own-agent connections within one shared brainstorm. Test the exact agent, permissions, and context flow your team needs."
      },
      {
        "question": "How were these Miro alternatives evaluated?",
        "answer": "We compared the same team-decision workflow across contribution, AI, facilitation, visual structure, convergence, integrations, governance, setup, and pricing. Capability and pricing claims were reviewed from primary sources on July 26, 2026."
      }
    ]
  }
];

export const publishedSeoPages: readonly SeoPageMetadata[] = [
  {
    "slug": "ai-assisted-decision-making",
    "type": "guide",
    "status": "published",
    "title": "AI-Assisted Decision Making: How Teams Make Better Decisions",
    "seoTitle": "AI-Assisted Decision Making for Teams: A Framework",
    "description": "Use AI to compare options and challenge assumptions while people own the decision. Includes a copyable decision template and a worked build-versus-buy example.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-09-07",
    "canonical": "https://boardblend.com/resources/ai-assisted-decision-making",
    "author": "Boardblend",
    "hero": {
      "src": "/seo/ai-assisted-decision-making/header.webp",
      "alt": "Tactile evidence streams passing through a compass toward a grounded shared decision"
    },
    "footer": {
      "visible": false
    },
    "faqs": [
      {
        "question": "Can AI make better decisions than a team?",
        "answer": "AI can improve parts of a process by organizing evidence, generating alternatives, and challenging assumptions. Decision quality still depends on valid context, accountable people, relevant expertise, and verification."
      },
      {
        "question": "What decisions should never be delegated entirely to AI?",
        "answer": "Do not delegate accountability for legal, medical, employment, safety, security, financial, or other high-impact decisions to a general AI system. Use qualified review, approved systems, and applicable governance."
      },
      {
        "question": "How can teams prevent automation bias?",
        "answer": "Capture independent judgments before revealing AI advice, show uncertainty and sources, require counterarguments, make override responsibility explicit, and track when people accept or reject model suggestions."
      },
      {
        "question": "Can multiple AI agents improve a team decision?",
        "answer": "Multiple agents can widen perspectives when they use relevant context and distinct roles. They can also repeat the same error, so compare evidence and assumptions rather than treating agreement between models as proof."
      },
      {
        "question": "How should a team measure decision quality?",
        "answer": "Track process quality, outcome quality, and learning speed. Review whether relevant perspectives entered, assumptions were tested, the expected outcome occurred, and the team updated its model when new evidence arrived."
      },
      {
        "question": "How does AI help in decision making?",
        "answer": "AI can organize evidence, generate alternatives, expose assumptions, compare scenarios, challenge a preferred option, and draft a decision record. People must still verify claims, weigh consequences, and own the decision."
      },
      {
        "question": "What is an example of AI-assisted decision making?",
        "answer": "A product team can give AI customer evidence, engineering constraints, and decision criteria, then use it to compare build, buy, and test options. A named human owner verifies the analysis and records the final choice."
      }
    ]
  },
  {
    "slug": "ai-brainstorming-tools",
    "type": "roundup",
    "status": "published",
    "title": "Best AI Brainstorming Tools for Teams in 2026",
    "seoTitle": "10 Best AI Brainstorming Tools for Teams (2026)",
    "description": "Compare the best free and paid AI brainstorming tools for team workshops, parallel ideation, shared context, visual collaboration, and clearer decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-09-07",
    "canonical": "https://boardblend.com/resources/ai-brainstorming-tools",
    "author": "Boardblend",
    "reviewedAt": "2026-08-01",
    "hero": {
      "src": "/seo/ai-brainstorming-tools/header.webp",
      "alt": "A tactile creative toolkit with idea cards, a lens, clustering rings, and a shared canvas"
    },
    "footer": {
      "visible": true,
      "label": "AI Brainstorming Tools",
      "order": 1
    },
    "faqs": [
      {
        "question": "What is the best AI brainstorming tool for teams?",
        "answer": "Boardblend is the focused option for parallel teammate and agent contributions that converge on one decision. Miro is stronger for broad whiteboarding, FigJam for design teams, and ChatGPT or Claude for individual exploration."
      },
      {
        "question": "What are the best free brainstorming tools for teams?",
        "answer": "Boardblend is a focused free option for one active team brainstorm, while Miro and FigJam provide broader free visual workspaces with plan limits. Compare collaboration, AI allowances, privacy, exports, and whether the workflow records a decision before choosing."
      },
      {
        "question": "Can teammates use different AI models in one brainstorm?",
        "answer": "Yes, if the workflow supports separate connections and approved data handling. Boardblend is designed around bring-your-own agents, while other tools may provide built-in AI, shared workspaces, or external connectivity."
      },
      {
        "question": "What features matter most for remote team brainstorming?",
        "answer": "Prioritize independent contribution, shared context, clear provenance, asynchronous access, facilitation, evidence links, structured evaluation, decision capture, permissions, accessibility, and reliable exports."
      },
      {
        "question": "How is an AI canvas different from an AI chat?",
        "answer": "A visual canvas keeps options, evidence, relationships, and disagreements visible at once. Chat is excellent for sequential exploration, but important context and parallel viewpoints can become buried in a transcript."
      },
      {
        "question": "How were these AI brainstorming tools evaluated?",
        "answer": "We applied one product-roadmap workshop to each category and compared participation, context, divergence, synthesis, visual structure, decision capture, facilitation, integrations, governance, setup, and value as of August 1, 2026."
      }
    ]
  },
  {
    "slug": "boardblend-vs-chatgpt",
    "type": "comparison",
    "status": "published",
    "title": "Boardblend vs ChatGPT for Team Brainstorming",
    "seoTitle": "Boardblend vs ChatGPT for Team Brainstorming",
    "description": "Compare Boardblend and ChatGPT for brainstorming, shared context, parallel team input, visual organization, multiple agents, and clearer team decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/boardblend-vs-chatgpt",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "competitor": "ChatGPT",
    "hero": {
      "src": "/seo/boardblend-vs-chatgpt/header.webp",
      "alt": "A deep sequential paper tunnel contrasted with parallel streams weaving into a shared workspace"
    },
    "footer": {
      "visible": true,
      "label": "Boardblend vs ChatGPT",
      "order": 5
    },
    "faqs": [
      {
        "question": "Is Boardblend an AI model?",
        "answer": "No. Boardblend is a collaborative visual workspace that coordinates people and the AI agents they choose. The quality and capabilities of an agent still depend on its underlying model and tools."
      },
      {
        "question": "Does Boardblend replace ChatGPT?",
        "answer": "Usually not. ChatGPT is a broad AI assistant for research, analysis, writing, coding, and individual exploration. Boardblend adds a team layer for parallel contribution, shared visual context, and decision convergence."
      },
      {
        "question": "Can teammates bring ChatGPT into Boardblend?",
        "answer": "Boardblend is designed for bring-your-own-agent connections, so ChatGPT can be part of a participant's workflow where a supported, approved connection is available. Verify the current integration and workspace policies."
      },
      {
        "question": "How is a visual canvas different from a shared ChatGPT project?",
        "answer": "A shared project centralizes files, instructions, and chats. A visual canvas keeps several participants' options, evidence, relationships, disagreements, and final rationale visible together rather than across chat branches."
      },
      {
        "question": "Which is better for individual brainstorming or team decisions?",
        "answer": "ChatGPT is generally better for flexible individual exploration. Boardblend is better when several people and agents need to contribute in parallel and preserve how the team reached a decision."
      }
    ]
  },
  {
    "slug": "boardblend-vs-figjam",
    "type": "comparison",
    "status": "published",
    "title": "Boardblend vs FigJam for Team Brainstorming",
    "seoTitle": "Boardblend vs FigJam for AI Team Brainstorming",
    "description": "Compare Boardblend and FigJam for AI brainstorming, visual collaboration, independent input, design workflows, cross-functional evidence, and decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/boardblend-vs-figjam",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "competitor": "FigJam",
    "hero": {
      "src": "/seo/boardblend-vs-figjam/header.webp",
      "alt": "Playful design pieces contrasted with cross-functional tokens forming a stable decision sculpture"
    },
    "footer": {
      "visible": false
    },
    "faqs": [
      {
        "question": "Is Boardblend a FigJam alternative?",
        "answer": "Boardblend is a focused FigJam alternative for AI-assisted cross-functional decisions, but not a complete replacement for design workshops, freeform drawing, Figma adjacency, or FigJam's multiplayer facilitation features."
      },
      {
        "question": "Which product is better for designers?",
        "answer": "FigJam is usually better for designers because it sits beside Figma and supports familiar visual ideation, critique, diagrams, reactions, voting, and direct movement toward design work."
      },
      {
        "question": "Does FigJam support AI brainstorming?",
        "answer": "Yes. FigJam AI can generate workshop materials, diagrams, timelines, and plans, then sort and summarize sticky notes. Availability and AI credits depend on current plans and seats."
      },
      {
        "question": "Which product handles cross-functional decisions better?",
        "answer": "Boardblend is more focused on separate product, design, engineering, and agent perspectives, visible disagreement, shared evidence, and a final rationale. FigJam can support this when a facilitator builds the process."
      },
      {
        "question": "Can Boardblend output move into Figma?",
        "answer": "Teams can use the Boardblend decision and rationale to guide Figma work, but verify current export or integration support for the exact handoff. FigJam remains the more native option inside the Figma ecosystem."
      }
    ]
  },
  {
    "slug": "boardblend-vs-lucidspark",
    "type": "comparison",
    "status": "published",
    "title": "Boardblend vs Lucidspark for Team Brainstorming",
    "seoTitle": "Boardblend vs Lucidspark: Team Workflow Compared",
    "description": "Compare Boardblend and Lucidspark for AI brainstorming, visual collaboration, facilitation, diagramming, shared evidence, and documented team decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/boardblend-vs-lucidspark",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "competitor": "Lucidspark",
    "hero": {
      "src": "/seo/boardblend-vs-lucidspark/header.webp",
      "alt": "Structured process blocks contrasted with evidence paths joining a shared decision hub"
    },
    "footer": {
      "visible": false
    },
    "faqs": [
      {
        "question": "Is Boardblend a Lucidspark alternative?",
        "answer": "Boardblend is a focused Lucidspark alternative for human-and-agent team decisions, but it does not replace Lucidspark's broader whiteboarding, facilitation, diagramming connections, and Lucid ecosystem."
      },
      {
        "question": "Which product is better for diagramming?",
        "answer": "Lucidspark is better for diagramming-oriented workflows, especially when ideas should move into Lucidchart, formal process maps, system diagrams, or structured visual documentation."
      },
      {
        "question": "Does Lucidspark have AI brainstorming features?",
        "answer": "Yes. Lucid AI can generate and expand ideas, sort content into themes, summarize sessions, build mind maps, and work with canvas context. Availability and future pricing may change."
      },
      {
        "question": "Which product is better for cross-functional decisions?",
        "answer": "Boardblend is more focused on separate functional and agent perspectives, shared evidence, visible disagreement, and a final rationale. Lucidspark can support decisions through facilitation, voting, tables, and task handoff."
      },
      {
        "question": "Can teams use Boardblend and Lucidspark together?",
        "answer": "Yes. Boardblend can hold the decision rationale while Lucidspark and Lucidchart document the resulting process or system. Name one authoritative record and link the artifacts."
      }
    ]
  },
  {
    "slug": "boardblend-vs-miro",
    "type": "comparison",
    "status": "published",
    "title": "Boardblend vs Miro for AI Team Brainstorming",
    "seoTitle": "Boardblend vs Miro: AI Team Brainstorming Compared",
    "description": "Compare Boardblend and Miro for AI brainstorming, shared context, visual collaboration, personal agent workflows, facilitation, and team decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/boardblend-vs-miro",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "competitor": "Miro",
    "hero": {
      "src": "/seo/boardblend-vs-miro/header.webp",
      "alt": "A sprawling visual workspace contrasted with a focused bridge carrying people and AI toward one outcome"
    },
    "footer": {
      "visible": true,
      "label": "Boardblend vs Miro",
      "order": 4
    },
    "faqs": [
      {
        "question": "Is Boardblend a complete replacement for Miro?",
        "answer": "No. Miro covers broader whiteboarding, diagramming, planning, prototypes, integrations, and enterprise administration. Boardblend is a focused alternative for parallel human-and-agent brainstorming that ends in a clear decision."
      },
      {
        "question": "Does Miro support external AI agents?",
        "answer": "Yes. Miro offers an MCP server on every plan with plan-specific call limits and supports AI tools such as Claude, Cursor, GitHub Copilot, and Gemini CLI."
      },
      {
        "question": "Which product is better for facilitated workshops?",
        "answer": "Miro is stronger for general facilitation because it offers mature timers, voting, private mode, visitor editing, templates, and activities. Boardblend is stronger when the workshop specifically centers on participant-owned agents and decision convergence."
      },
      {
        "question": "Which product is simpler for AI-assisted team decisions?",
        "answer": "Boardblend is more focused and requires less process design for its core use case. Miro can run a strong decision workshop, but the facilitator chooses and configures the board, modes, AI actions, and decision template."
      },
      {
        "question": "Can a team use Boardblend and Miro together?",
        "answer": "Yes. A team can retain Miro for broad visual planning and use Boardblend for consequential AI-assisted decisions. Avoid duplicating the authoritative decision record across both products."
      }
    ]
  },
  {
    "slug": "boardblend-vs-mural",
    "type": "comparison",
    "status": "published",
    "title": "Boardblend vs Mural for AI-Assisted Teamwork",
    "seoTitle": "Boardblend vs Mural: AI Decisions or Workshops?",
    "description": "Compare Boardblend and Mural for AI-assisted brainstorming, workshop facilitation, shared context, parallel participation, and documented team decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/boardblend-vs-mural",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "competitor": "Mural",
    "hero": {
      "src": "/seo/boardblend-vs-mural/header.webp",
      "alt": "A facilitated workshop table contrasted with parallel contributors gathering around a shared outcome"
    },
    "footer": {
      "visible": false
    },
    "faqs": [
      {
        "question": "Is Boardblend a complete Mural replacement?",
        "answer": "No. Mural offers broader facilitation controls, templates, visual methods, enterprise administration, and Microsoft ecosystem connections. Boardblend is a focused alternative for participant-owned agents and traceable team decisions."
      },
      {
        "question": "Which product is better for professional facilitators?",
        "answer": "Mural is generally stronger for professional facilitators because of private mode, anonymous voting, timer, summon and control features, hide and reveal, templates, and established workshop methods."
      },
      {
        "question": "Does Mural have AI brainstorming features?",
        "answer": "Yes. As reviewed July 26, 2026, Mural AI can generate and expand ideas, create mind maps, cluster content, summarize, classify sentiment, and support guided AI workflows on paid plans."
      },
      {
        "question": "Which product supports teammate-owned AI agents?",
        "answer": "Boardblend is designed so each participant can connect a preferred AI agent to the shared brainstorm. Mural documents one integrated Mural AI experience and Microsoft 365 Copilot connections rather than participant-specific parallel agents."
      },
      {
        "question": "Which product is better for documenting decisions?",
        "answer": "Boardblend is more opinionated about preserving source perspectives, trade-offs, rationale, dissent, owner, and next action. Mural can document decisions well when the facilitator builds that structure into the workshop."
      }
    ]
  },
  {
    "slug": "feature-prioritization",
    "type": "guide",
    "status": "published",
    "title": "How to Prioritize Features With Product, Design and Engineering",
    "seoTitle": "Feature Prioritization With Product, Design & Engineering",
    "description": "Prioritize features using developer feedback, customer evidence, and clear low, medium, and high scores. Includes a copyable worksheet and worked example.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-09-07",
    "canonical": "https://boardblend.com/resources/feature-prioritization",
    "author": "Boardblend",
    "hero": {
      "src": "/seo/feature-prioritization/header.webp",
      "alt": "Four hands contributing product, design, and engineering tokens to a balanced feature priority"
    },
    "footer": {
      "visible": true,
      "label": "Feature Prioritization",
      "order": 6
    },
    "faqs": [
      {
        "question": "What is the best feature prioritization framework?",
        "answer": "Use the lightest framework that makes the real trade-off visible. Value versus effort works for a small uncertain set, RICE helps compare reach and impact, and weighted scoring helps when several explicit constraints matter."
      },
      {
        "question": "What role should engineering play in feature prioritization?",
        "answer": "Engineering should contribute delivery ranges, dependencies, operational risks, reversibility, platform leverage, and simpler alternatives. It should not be asked only to estimate a solution after priorities are already decided."
      },
      {
        "question": "How should teams resolve conflicting feature scores?",
        "answer": "Discuss the evidence, definition, confidence, and assumptions behind the scores before averaging them. A large difference often reveals useful domain knowledge or a differently understood problem."
      },
      {
        "question": "When should a product team use RICE prioritization?",
        "answer": "RICE is useful when initiatives have reasonably comparable estimates for reach, impact, confidence, and effort. Avoid it when inputs are mostly guesses or when strategic, regulatory, or dependency constraints dominate."
      },
      {
        "question": "Can AI prioritize a product backlog?",
        "answer": "AI can organize evidence, challenge assumptions, and calculate an agreed model. People must still validate the inputs, choose the criteria, own the trade-offs, and record the decision."
      }
    ]
  },
  {
    "slug": "groupthink-examples",
    "type": "guide",
    "status": "published",
    "title": "Groupthink Examples—and How to Avoid It During Team Brainstorming",
    "seoTitle": "Groupthink Examples—and How to Avoid It at Work",
    "description": "See practical groupthink examples from everyday team decisions, learn the warning signs, and use a repeatable process that protects independent thinking.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/groupthink-examples",
    "author": "Boardblend",
    "hero": {
      "src": "/seo/groupthink-examples/header.webp",
      "alt": "Identical paper planes following one route contrasted with distinct paths reaching a shared destination"
    },
    "footer": {
      "visible": false
    },
    "faqs": [
      {
        "question": "What is a simple example of groupthink at work?",
        "answer": "A product team agrees with the first feature proposed by its leader, even though several members privately see customer, design, or technical risks. The apparent consensus reflects social pressure and anchoring rather than independent agreement."
      },
      {
        "question": "What are the main warning signs of groupthink?",
        "answer": "Common signs include unusually fast agreement, few alternatives, self-censorship, pressure on dissenters, confidence without evidence, and a decision record that contains no risks or minority views."
      },
      {
        "question": "Does groupthink mean a team should avoid consensus?",
        "answer": "No. Consensus can be a useful outcome. The risk is premature consensus reached before people have contributed independent information, challenged assumptions, and compared alternatives against explicit criteria."
      },
      {
        "question": "How can a facilitator reduce groupthink during brainstorming?",
        "answer": "Ask people to think independently before discussion, collect contributions in parallel, delay the leader’s opinion, assign a challenger, evaluate ideas against shared criteria, and record unresolved concerns with the final decision."
      },
      {
        "question": "Can AI make groupthink worse?",
        "answer": "Yes. If everyone sees the same AI answer first, it can become a powerful anchor. AI is safer when teammates use it to develop or challenge independent perspectives before the group combines and evaluates the results."
      }
    ]
  },
  {
    "slug": "how-to-brainstorm-with-ai",
    "type": "guide",
    "status": "published",
    "title": "How to Brainstorm With AI as a Team",
    "seoTitle": "How to Brainstorm With AI as a Team: 7 Steps",
    "description": "Use a practical template and prompts to brainstorm with AI as a team, preserve independent perspectives, and finish with a clear decision.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-09-07",
    "canonical": "https://boardblend.com/resources/how-to-brainstorm-with-ai",
    "author": "Boardblend",
    "hero": {
      "src": "/seo/how-to-brainstorm-with-ai/header.webp",
      "alt": "Team hands and small luminous AI companions arranging distinct ideas on one shared workspace"
    },
    "footer": {
      "visible": true,
      "label": "Brainstorm With AI",
      "order": 2
    },
    "faqs": [
      {
        "question": "Should people brainstorm independently before using AI?",
        "answer": "Yes. A short independent pass protects each person’s knowledge and judgment from being anchored by the first visible AI answer. AI can then expand, challenge, or test those original views."
      },
      {
        "question": "Can teammates use different AI agents in one brainstorm?",
        "answer": "Yes. Different agents can contribute useful perspectives when they receive the same decision context and their outputs remain attributable. The team should compare evidence and assumptions rather than vote for a favourite model."
      },
      {
        "question": "What context should an AI brainstorming session include?",
        "answer": "Give every participant the decision, desired outcome, audience, evidence, constraints, known risks, and definition of success. Exclude confidential data that the selected AI service is not approved to process."
      },
      {
        "question": "How long should an AI team brainstorm take?",
        "answer": "A focused decision can use the 30-minute agenda in this guide. Complex or high-risk decisions need more time for evidence gathering, fact-checking, stakeholder review, and a separate approval step."
      },
      {
        "question": "How do teams prevent AI-driven convergence?",
        "answer": "Capture independent human views first, let agents challenge rather than replace them, delay synthesis until all contributions are visible, and preserve disagreements alongside the final decision."
      },
      {
        "question": "Can I use AI to brainstorm ideas without letting it take over?",
        "answer": "Yes. Write your initial view first, then ask AI for different mechanisms, missing stakeholders, counterarguments, and evidence gaps. Keep the original human contribution visible and verify factual claims before using them."
      }
    ]
  },
  {
    "slug": "miro-alternatives",
    "type": "roundup",
    "status": "published",
    "title": "The Best Miro Alternatives for AI Brainstorming",
    "seoTitle": "7 Best Miro Alternatives for AI Brainstorming (2026)",
    "description": "Compare Miro alternatives for AI team brainstorming, visual collaboration, facilitation, shared context, agent workflows, pricing, and clearer decisions.",
    "publishedAt": "2026-07-26",
    "updatedAt": "2026-07-26",
    "canonical": "https://boardblend.com/resources/miro-alternatives",
    "author": "Boardblend",
    "reviewedAt": "2026-07-26",
    "hero": {
      "src": "/seo/miro-alternatives/header.webp",
      "alt": "Many wandering creative paths resolving into one focused green route toward a clear outcome"
    },
    "footer": {
      "visible": true,
      "label": "Miro Alternatives",
      "order": 3
    },
    "faqs": [
      {
        "question": "What is the best Miro alternative for AI brainstorming?",
        "answer": "Boardblend is the focused choice when teammates and their preferred AI agents need to contribute in parallel and converge on a shared decision. Mural, FigJam, and Lucidspark are stronger fits for different workshop, design, or diagramming needs."
      },
      {
        "question": "Does Miro support AI and MCP?",
        "answer": "Yes. As of July 26, 2026, Miro offers AI generation, clustering, summaries, workflows, agents, and an MCP server on every plan with plan-specific credit and call limits."
      },
      {
        "question": "Which Miro alternatives support free visual collaboration?",
        "answer": "Miro, FigJam, Lucidspark, Whimsical, Excalidraw, ClickUp, and Boardblend all provide some form of free access or trial, but limits and guest rules vary. Verify current pricing before choosing."
      },
      {
        "question": "Can a team use personal AI agents instead of built-in whiteboard AI?",
        "answer": "Miro supports external AI tools through MCP. Boardblend focuses on participant-specific bring-your-own-agent connections within one shared brainstorm. Test the exact agent, permissions, and context flow your team needs."
      },
      {
        "question": "How were these Miro alternatives evaluated?",
        "answer": "We compared the same team-decision workflow across contribution, AI, facilitation, visual structure, convergence, integrations, governance, setup, and pricing. Capability and pricing claims were reviewed from primary sources on July 26, 2026."
      }
    ]
  }
];

export const footerSeoPages: readonly FooterSeoPage[] = [
  {
    "label": "AI Brainstorming Tools",
    "order": 1,
    "slug": "ai-brainstorming-tools"
  },
  {
    "label": "Brainstorm With AI",
    "order": 2,
    "slug": "how-to-brainstorm-with-ai"
  },
  {
    "label": "Miro Alternatives",
    "order": 3,
    "slug": "miro-alternatives"
  },
  {
    "label": "Boardblend vs Miro",
    "order": 4,
    "slug": "boardblend-vs-miro"
  },
  {
    "label": "Boardblend vs ChatGPT",
    "order": 5,
    "slug": "boardblend-vs-chatgpt"
  },
  {
    "label": "Feature Prioritization",
    "order": 6,
    "slug": "feature-prioritization"
  }
];
